import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import { isJSONDocNode } from '@atlaskit/editor-json-transformer/main';

import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { prepareGuardExceptionTask } from '@confluence/query-preloader-tools/entry-points/prepareGuardExceptionTask.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { preloadOutgoingLinks } from '@confluence/connectors/entry-points/preloadOutgoingLinks.ts';
import { preloadContent } from '@confluence/content-body/entry-points/preloadContent.ts';
import { preloadContentHeader } from '@confluence/content-header/entry-points/preloadContentHeader.ts';
import { preloadContentPrerequisites } from '@confluence/content-prerequisites/entry-points/preloadContentPrerequisites.ts';
import { preloadDraftRedirect } from '@confluence/content-prerequisites/entry-points/preloadDraftRedirect.ts';
import { preloadContentSmartLinks } from '@confluence/content-smartlinks/entry-points/preloadContentSmartLinks.ts';
import { preloadContentSmartLinksWithSuspense } from '@confluence/content-smartlinks/entry-points/preloadContentSmartLinksWithSuspense.ts';
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader.js';
import { preloadPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadPageTitleContentProperties.ts';
import { preloadAutohighlights } from '@confluence/contextual-reading-aids/entry-points/preloadAutohighlights.ts';
import { preloadCustomHeaderAndFooter } from '@confluence/custom-header-footer/entry-points/preloadCustomHeaderAndFooter.ts';
import { preloadMacrosSSR } from '@confluence/fabric-extension-handlers/entry-points/preloadMacrosSSR.ts';
import { preloadMacroData } from '@confluence/preload-macros/entry-points/preloadMacroData.ts';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken.ts';
import { preloadIsAttachmentDownloadEnabled } from '@confluence/fabric-media-support/entry-points/preloadIsAttachmentDownloadEnabled.ts';
import { preloadFocusedInlineComment } from '@confluence/inline-comments/entry-points/preloadFocusedInlineComment.ts';
import { preloadAIFloatingContextMenu } from '@confluence/ai-floating-context-menu/entry-points/preloadAIFloatingContextMenu.ts';
import { preloadSmartCreateFeedbackBanner } from '@confluence/editor-action-bar/entry-points/preloadSmartCreateFeedbackBanner.ts';
import {
	COMPANY_HUB_EDIT,
	EDIT_BLOG_V2,
	EDIT_PAGE_V2,
	HUB_EDIT,
} from '@confluence/named-routes/entry-points/routes.ts';
import { preloadEndOfPageRecommendation } from '@confluence/page-recommendations/entry-points/preloadEndOfPageRecommendation.ts';
import { preloadRecordLoomButton } from '@confluence/loom-utils/entry-points/preloadRecordLoomButton.ts';
import { preloadPresenceSettings } from '@confluence/team-presence/entry-points/preloadPresenceSettings.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { preloadPageReactions } from '@confluence/reactions/entry-points/preloadPageReactions.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points/index.ts';
import { preloadPageStatusQuery } from '@confluence/query-preloaders-common/entry-points/preloadPageStatusQuery.ts';
import { preloadLegacyBridge } from '@confluence/query-preloaders-common/entry-points/preloadLegacyBridge.ts';
import {
	ATL_PAGE_CONTENT_FOOTER_ACTIONS,
	ATL_GENERAL,
	ATL_FOOTER,
	SYSTEM_CONTENT_BYLINE,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import {
	preloadWebPanelLocation,
	preloadWebPanelLocations,
} from '@confluence/web-panel-location/entry-points/preloadWebPanelLocation.ts';
import { fg } from '@confluence/feature-gating';
import {
	expVal,
	expValEquals,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import {
	FORGE_MODULE_BYLINE,
	FORGE_MODULE_XEN_MACRO,
} from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery.ts';
import { preloadRequestViewQuery } from '@confluence/no-permission/entry-points/preloadRequestViewQuery.ts';
import { preloadPendingRequestExists } from '@confluence/no-permission/entry-points/preloadPendingRequestExists.ts';
import { preloadIsSuperAdmin } from '@confluence/super-admin/entry-points/preloadIsSuperAdmin.ts';
import { preloadObjectComments } from '@confluence/object-comments/entry-points/preloadObjectComments.ts';
import { preloadOmnibar } from '@confluence/experiment-omnibar/entry-points/preloadOmnibar.ts';
import { preloadAddCommentPermissionCheck } from '@confluence/comments-panel-queries/entry-points/preloadAddCommentPermissionCheck.ts';
import { preloadLabels } from '@confluence/labels/entry-points/preloadLabels.tsx';
import { preloadViewPageOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadViewPageOnboardingState.ts';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation.ts';
import {
	getIsEmbeddedConfluence,
	getUsername,
	hasEmbeddedAllowedFeatures,
	shouldPreloadNavigationForTransition,
} from '@confluence/query-preloaders-common/entry-points/matchHelpers.ts';
import { matchBlogPage } from '@confluence/query-preloaders-common/entry-points/matchRoutes.ts';
import { preloadNavigationTasks } from '@confluence/query-preloaders-common/entry-points/preloadNavigationTasks.ts';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { preloadReadTime } from '@confluence/read-time/entry-points/preloadReadTime.ts';
import { preloadPageRestrictionsQuery } from '@confluence/page-restrictions-context/entry-points/preloadPageRestrictionsQuery.ts';
import { CONFLUENCE_EDITION } from '@confluence/content-header/entry-points/defaultQueryVariables.js';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { preloadContentTitleWithByLine } from '@confluence/view-page-common/entry-points/preloadContentTitleWithByLine.ts';
import { preloadContentMode } from '@confluence/editor-content-mode-button/entry-points/preloadContentMode.ts';
import type { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { preloadSyncedBlocksData } from '@confluence/load-edit-page/entry-points/preloadSyncedBlocksData.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { preloadAudioContent } from '@confluence/audio/entry-points/preloadAudioContent.ts';
import { preloadApprovals } from '@confluence/approvals/entry-points/preloadApprovals.ts';
import { preloadIncomingLinksSsrState } from '@confluence/byline-connections/entry-points/preloadIncomingLinksSsrState.ts';
import { preloadOutboundBylineSsrState } from '@confluence/byline-connections/entry-points/preloadOutboundBylineSsrState.ts';

import UnifiedUserQuery from './queries/__generated__/UnifiedUserQuery.graphql.ts';

const DEFAULT_PRELOAD_RECOMMENDATION_TIMEOUT = 700;

const createPreloadContentTask = (
	contentId: string,
	isBlog: boolean,
	spaceKey: string,
	cloudId: string,
	contentNodesPromiseResolve?: (value: any) => void,
	userId?: string | null,
	agentSessionsEnabled?: boolean,
): Promise<any> => {
	return prepareGuardExceptionTask('Content', () =>
		preloadContent({
			contentId,
			isBlog,
			spaceKey,
			userId,
			agentSessionsEnabled,
		}).then(async ({ result, hasErrors }): Promise<any> => {
			contentNodesPromiseResolve?.(result?.data?.content?.nodes);

			// Report the content query's own errors as SSR prepare errors. `preloadContent` runs with
			// `errorPolicy: 'all'`, so a failing content query resolves rather than rejecting, and this
			// task resolves with its follow-up preloads instead of the query result — so nothing ever
			// inspected `result.errors`, and a page whose body failed to load reached the renderer with
			// nothing logged anywhere. Passing the settled result (rather than a task) is intentional:
			// `waitForAllReturnWhenError` reads the `{ hasErrors, errors }` shape that `query()`
			// resolves with, and skips the expected shapes (restricted page, not found) that `query()`
			// has already marked as handled — so only unexpected failures are reported.
			await waitForAllReturnWhenError(result);

			if (hasErrors) {
				const restrictedPageTasks: Promise<unknown>[] = [
					preloadContentPrerequisites(contentId, spaceKey),
					preloadRequestViewQuery(contentId),
					preloadDraftRedirect({
						contentId,
						draftShareId: window.location.search.match(/draftShareId=(.*?)(#|&|$)/)?.[1] ?? '',
					}),
				];

				// Preload the pending-access-request check so the restricted-content screen can
				// render the resolved title/message during SSR instead of blanking them
				if (
					userId &&
					cloudId &&
					UNSAFE_expValEqualsNoExposure(
						'cc_mohiti_restricted_content_screen_ab_exp',
						'isEnabled',
						true,
					) &&
					UNSAFE_expValEqualsNoExposure(
						'confluence_frontend_existing_access_request_checks',
						'cohort',
						'experiment',
					)
				) {
					restrictedPageTasks.push(
						slowQueryGuard(
							'preloadPendingRequestExists',
							() => preloadPendingRequestExists(contentId, cloudId, userId),
							SLOW_QUERY_DEFAULT_TIMEOUT,
						),
					);
				}

				return waitForAllReturnWhenError(restrictedPageTasks);
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			} else if (process.env.REACT_SSR) {
				// No errors & no content means the page may be an unpublished draft
				if (result?.data?.content?.nodes?.length === 0) {
					return preloadDraftRedirect({
						contentId,
						draftShareId: window.location.search.match(/draftShareId=(.*?)(#|&|$)/)?.[1] ?? '',
					});
				}

				let parsedAdf: JSONDocNode | null = null;

				try {
					const adf = JSON.parse(result.data.content.nodes[0]?.body.dynamic.value);

					if (isJSONDocNode(adf)) {
						parsedAdf = adf;
					}
				} catch (error) {
					// JSON.parse error or unexpected data structure,
					// assume no synced blocks to avoid blocking page load

					ssrPrepareErrorsClient.submitError(
						new Error('Skipped synced blocks preload: failed to parse ADF document'),
						{
							contentId,
							cloudId,
							isBlog,
							errorMessage: error instanceof Error ? error.message : String(error),
						},
					);
				}

				if (parsedAdf) {
					return waitForAllReturnWhenError([
						preloadSyncedBlocksData({
							contentId,
							cloudId: cloudId || '',
							isBlog,
							doc: parsedAdf,
							measureSegment: 'ssr-app/prepare/preloadQuery/renderer/syncedBlocksData',
						}),
						preloadMacrosSSR({
							contentId,
							cloudId,
							useMultipleMacrosQuery: false,
							contentNodes: result?.data?.content?.nodes,
						}),
					]);
				}

				return preloadMacrosSSR({
					contentId,
					useMultipleMacrosQuery: false,
					contentNodes: result?.data?.content?.nodes,
				});
			}
		}),
	);
};

const createContentMainTasks = (params: {
	spaceKey: string;
	contentId: string;
	username?: string;
	edition: ConfluenceEdition | null;
	isPrevEditRoute: boolean;
	isBlog: boolean;
	cloudId?: string;
	contentNodesPromiseResolve?: (value: any) => void;
	contentNodesPromise: Promise<any>;
	isInitialLoad?: boolean;
	userId?: string | null;
	agentSessionsEnabled?: boolean;
}): Promise<unknown>[] => {
	const {
		spaceKey,
		contentId,
		username,
		edition,
		isPrevEditRoute,
		isBlog,
		cloudId,
		contentNodesPromiseResolve,
		contentNodesPromise,
		isInitialLoad,
		userId,
		agentSessionsEnabled,
	} = params;
	return [
		preloadContentHeader({
			spaceKey,
			contentId,
			username,
			edition,
			isPrevEditRoute,
			useNewContentTopper: true,
			isContentTypePublicLinkEligible: !isBlog,
		}),
		...(fg('confluence_vp_title_byline_frag_dedup')
			? []
			: [
					preloadContentTitleWithByLine(contentId, spaceKey),
					preloadPageTitleContentProperties({ contentId, spaceKey }),
				]),
		prepareGuardExceptionTask('Macros', () =>
			preloadMacrosSSR({
				contentId,
				useMultipleMacrosQuery: true,
				contentNodes: contentNodesPromise,
			}),
		),
		prepareGuardExceptionTask('MacroData', () =>
			preloadMacroData({
				contentNodes: contentNodesPromise,
				context: { contentId, spaceKey },
				route: 'view',
			}),
		),
		createPreloadContentTask(
			contentId,
			isBlog,
			spaceKey,
			cloudId || '',
			contentNodesPromiseResolve,
			userId,
			agentSessionsEnabled,
		),
		preloadPageStatusQuery(contentId),
		preloadLegacyBridge({ contentId, isBlog }),
		preloadReadTime({ contentId, includeContentBody: false, useCache: true }),
		// SmartLinks: on initial load, keep original non-suspense fetcher behavior when the suspense
		// experiment is disabled. SmartLinks are never preloaded on transitions.
		...(isInitialLoad && !expVal('confluence_smartlinks_suspense', 'isEnabled', false)
			? [preloadContentSmartLinks(contentId)]
			: []),
	];
};

/*
 * preloadViewPageRoute preload queries are currently arranged in order of latency, with slowest
 * queries pushed to the tasks array first.
 */
export const preloadViewPageRoute = async (
	match: RouteMatch,
	url: string,
	isTransition: boolean = false,
	previousMatch: RouteMatch | null = null,
): Promise<boolean | any[] | undefined> => {
	const {
		isLicensed,
		userId,
		edition,
		cloudId,
		accessStatus,
		isLoggedIn,
		environment,
		isAdminHubAIEnabled,
		activationId,
	} = getPreloaderFnContext();
	const tasks: Promise<any>[] = [];
	// following functions will be triggered after we load data
	const postLoadingTasks: (() => Promise<any>)[] = [];

	const { spaceKey, contentId } = match.params;
	const username = getUsername(match);
	const isEmbeddedConfluence = getIsEmbeddedConfluence(match);
	const isBlog = Boolean(matchBlogPage(url));
	const isInitialLoad = !isTransition;
	// This guard schedules server-only preload work; it does not alter the React tree.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isReactSsr = Boolean(process.env.REACT_SSR);
	const shouldPreloadNavigation =
		!isEmbeddedConfluence && (isInitialLoad || shouldPreloadNavigationForTransition(previousMatch));
	const shouldPreloadBylineConnections =
		isReactSsr &&
		isInitialLoad &&
		isLoggedIn &&
		Boolean(cloudId) &&
		Boolean(contentId) &&
		!isBlog &&
		// The existing byline UI owns exposure for this experiment.
		// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
		UNSAFE_expValEqualsNoExposure(
			'confluence_1p_and_3p_connection_byline_experiment',
			'isEnabled',
			true,
		);

	// Get isArchived from cached page data
	const { isArchived } = getCachedPageData(contentId);

	// Don't preload queries that will be updated by editor if previous route is edit_page
	const isPrevEditRoute =
		previousMatch?.name === EDIT_PAGE_V2.name ||
		previousMatch?.name === EDIT_BLOG_V2.name ||
		previousMatch?.name === COMPANY_HUB_EDIT.name ||
		previousMatch?.name === HUB_EDIT.name;

	if (isPrevEditRoute && isTransition) {
		// Preload for edit -> view transition
		return preloadContentHeader({
			spaceKey,
			contentId,
			username,
			edition,
			isPrevEditRoute,
			useNewContentTopper: true,
			isContentTypePublicLinkEligible: !isBlog,
		});
	}

	if (fg('confluence_preload_user_data')) {
		tasks.push(query({ query: UnifiedUserQuery }));
	}

	// ViewPageGateway wraps the entire page and renders nothing until useIsSuperAdmin settles, so
	// this has to resolve before render or no content is emitted. See preloadIsSuperAdmin for the
	// conditions under which it issues a request; it is a no-op for everyone else.
	tasks.push(preloadIsSuperAdmin());

	if (!isPrevEditRoute) {
		tasks.push(preloadPageRestrictionsQuery(contentId, edition === CONFLUENCE_EDITION.FREE));
	}

	if (
		isLoggedIn &&
		edition === CONFLUENCE_EDITION.PREMIUM &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true)
	) {
		tasks.push(preloadApprovals({ cloudId, contentId, contentStatus: 'CURRENT', spaceKey }));
	}

	// Content types header (object header) + Unified Share and Restrict Button
	tasks.push(
		createDataFetcherForSuspense('content-types-header', () => {
			const contentTypesHeaderPreloadTasks: Promise<any>[] = [];
			if (!fg('confluence_vp_access_frag_reader')) {
				if (fg('confluence_frontend_usd_ssr')) {
					contentTypesHeaderPreloadTasks.push(preloadShareAndRestrictButtonQuery(contentId));
				}
			}
			contentTypesHeaderPreloadTasks.push(preloadContentTypesHeader(contentId));

			// preload data for whether "unpublished changes" label should show in view page object header
			contentTypesHeaderPreloadTasks.push(
				preloadWebItemLocation({
					location: 'page.metadata.banner',
					contentId,
				}),
			);
			// (No need to call preloadPageRestrictionsQuery() here; it will be called by
			//  preloadContentHeader() )
			return contentTypesHeaderPreloadTasks;
		}),
	);

	// Content main
	let contentNodesPromiseResolve: ((value: any) => void) | undefined = undefined;
	const contentNodesPromise = new Promise<any>((resolve) => {
		contentNodesPromiseResolve = resolve;
	});
	tasks.push(
		createDataFetcherForSuspense('content-main', () => {
			const mainTasks = createContentMainTasks({
				spaceKey,
				contentId,
				username,
				edition,
				isPrevEditRoute,
				isBlog,
				cloudId,
				contentNodesPromiseResolve,
				contentNodesPromise,
				isInitialLoad,
				userId,
				// confluence-feature-gating/inline-usage: must be inlined
				agentSessionsEnabled: expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true),
			});

			if (
				isReactSsr &&
				isInitialLoad &&
				expVal('confluence_measure_byline_preload_perf', 'isEnabled', false)
			) {
				mainTasks.push(
					preloadWebItemLocation({
						contentId,
						location: SYSTEM_CONTENT_BYLINE,
					}),
					preloadUseExtensionList({
						contentId,
						spaceKey,
						moduleType: FORGE_MODULE_BYLINE,
					}),
				);
			}

			if (shouldPreloadBylineConnections) {
				mainTasks.push(
					preloadIncomingLinksSsrState({
						cloudId,
						contentId,
						route: 'view',
					}),
				);
				mainTasks.push(preloadOutboundBylineSsrState(contentId));
			}

			if (isInitialLoad && isLoggedIn) {
				mainTasks.push(
					createDataFetcherForSuspense('end-of-page-recommendations', () => {
						const recommendationTasks: Promise<any>[] = [
							preloadEndOfPageRecommendation({
								contentId,
								spaceKey,
								contentType: isBlog ? 'blogpost' : 'page',
							}),
						];
						if (
							!isBlog &&
							// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
							UNSAFE_expValEqualsNoExposure(
								'confluence_3p_in_related_content',
								'shouldEvaluate',
								true,
							)
						) {
							recommendationTasks.push(preloadOutgoingLinks(contentId));
						}
						return recommendationTasks;
					}),
				);
			}

			return mainTasks;
		}),
	);

	// SmartLinks preloaded with Suspense data fetcher outside of content-main suspense boundary
	if (isInitialLoad && expVal('confluence_smartlinks_suspense', 'isEnabled', false)) {
		void preloadContentSmartLinksWithSuspense(contentId);
	}

	if (!fg('confluence_vp_mode_frag_reader')) {
		tasks.push(preloadContentMode({ contentId, isDraft: false, useCache: true, isArchived }));
	}

	// Navigation, Page tree and blog tree for blog post
	if (shouldPreloadNavigation) {
		tasks.push(
			createDataFetcherForSuspense('side-navigation', () => {
				return preloadNavigationTasks(
					spaceKey,
					contentId,
					isLicensed,
					isBlog,
					previousMatch?.params?.spaceKey,
				);
			}),
		);
	}
	// Space details
	tasks.push(preloadSpaceDetail(spaceKey));

	// Inline Comments
	if (match?.query.focusedCommentId) {
		tasks.push(preloadFocusedInlineComment(match?.query.focusedCommentId as string, contentId));
	}

	// Reactions
	if (!isEmbeddedConfluence || hasEmbeddedAllowedFeatures('page-reactions', match)) {
		// Preload whether the server has enabled reactions at all to show placeholder
		tasks.push(preloadWebPanelLocation({ contentId, location: ATL_PAGE_CONTENT_FOOTER_ACTIONS }));
		// Preload reactions asynchronously so SSR won't wait for it
		// it's best effort preloading so if it preloads then we have the data, otherwise SPA can load it
		if (!fg('confluence_vp_reactions_frag_reader')) {
			tasks.push(
				preloadPageReactions({
					pageId: contentId,
					containerId: spaceKey,
				}),
			);
		}
	}

	// Recommendations
	// On initial load these are preloaded inside the 'end-of-page-recommendations' suspense data
	// fetcher above, so only transitions need this standalone preload. Recommendations are only
	// preloaded for authenticated users (knowledge_discovery service requires auth).
	if (!isInitialLoad && isLoggedIn) {
		const recommendationsTimeout = StatsigConfigurations.getNumber(
			'confluence_frontend_preload_recommendation_timeout',
			DEFAULT_PRELOAD_RECOMMENDATION_TIMEOUT,
		);
		tasks.push(
			slowQueryGuard(
				'preloadEndOfPageRecommendation',
				() => {
					return preloadEndOfPageRecommendation({
						contentId,
						spaceKey,
						contentType: isBlog ? 'blogpost' : 'page',
					});
				},
				recommendationsTimeout,
			),
		);
	}

	// Stuff only needed for initial load
	if (isInitialLoad) {
		tasks.push(
			// We only refresh token when it is about to expire.
			preloadMediaToken(contentId, cloudId || ''),
		);
		tasks.push(preloadCustomHeaderAndFooter(spaceKey));
		if (fg('dlp_attachment_download_respects_data_export_dsp')) {
			tasks.push(preloadIsAttachmentDownloadEnabled(contentId, spaceKey));
		}
	}

	// Preload onboarding state for view page
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
			accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS) &&
		!isTransition &&
		expVal('confluence_centralized_onboarding_state', 'isEnabled', false)
	) {
		tasks.push(preloadViewPageOnboardingState());
	}

	// Labels
	if (isTransition) {
		tasks.push(preloadLabels({ contentId }));
	}

	// Preload web panel atl_general
	if (expValEquals('web_panel_query_batch_call', 'isEnabled', true)) {
		tasks.push(
			preloadWebPanelLocations({
				contentId,
				locations: [ATL_GENERAL, ATL_FOOTER],
			}),
		);
	} else {
		tasks.push(
			preloadWebPanelLocation({ contentId, location: ATL_GENERAL }),
			preloadWebPanelLocation({ contentId, location: ATL_FOOTER }),
		);
	}

	// Object Sidebar Control + AI Button
	if (isAdminHubAIEnabled) {
		tasks.push(
			createDataFetcherForSuspense('ai-floating-context-menu', () =>
				preloadAIFloatingContextMenu(contentId, isBlog),
			),
		);
	}

	// Preload Audio Listen Button
	tasks.push(preloadAudioContent(contentId));

	const extensionListLoader = () =>
		preloadUseExtensionList({
			contentId,
			spaceKey,
			moduleType: FORGE_MODULE_XEN_MACRO,
		});

	tasks.push(extensionListLoader());

	// Preload Loom button in Action Bar
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS) &&
		isLoggedIn
	) {
		tasks.push(
			preloadRecordLoomButton({
				cloudId,
				activationId,
				accessStatus,
				isLoggedIn,
				userId,
				environment,
			}),
		);
	}

	// Presence Avatar Visibility
	tasks.push(preloadPresenceSettings({ cloudId, spaceKey }));

	// Preload definition autohighlights
	// Only preload autohighlights for authenticated users (knowledge_discovery service requires auth)
	if (isLoggedIn) {
		tasks.push(
			preloadAutohighlights({
				contentId,
				cloudId,
				activationId,
				contentType: isBlog ? 'blogpost' : 'page',
			}),
		);
	}

	tasks.push(
		createDataFetcherForSuspense('object-comments', () => [
			// Preload footer comments. SSRing live pages necessitates non-server (i.e. transition)
			// preloading, so enable here too for consistency in the footer component.
			preloadObjectComments(contentId),
			// Preload add comment permission check for the footer comment
			preloadAddCommentPermissionCheck(contentId, ['draft', 'current']),
		]),
	);

	// Preload onboarding state for view page (only if not already preloaded by centralized experiment)
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
			accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS) &&
		expVal('confluence_pgxp_p2m_batch_1', 'isEnabled', false)
	) {
		if (isTransition || !expVal('confluence_centralized_onboarding_state', 'isEnabled', false)) {
			tasks.push(preloadViewPageOnboardingState());
		}
	}

	if (fg('cc-ai-growth-preload-omnibar')) {
		tasks.push(preloadOmnibar());
	}

	// Preload Smart Create Feedback Banner
	tasks.push(preloadSmartCreateFeedbackBanner(contentId));

	const errors = await waitForAllReturnWhenError(tasks);
	const postLoadingErrors = await waitForAllReturnWhenError(postLoadingTasks.map((task) => task()));

	if (errors || postLoadingErrors) {
		return [...(errors || []), ...(postLoadingErrors || [])];
	}
};
